import { useEffect, useRef } from 'react';

interface ParticleNetworkProps {
  mouseX: number;
  mouseY: number;
}

interface Particle {
  x: number;
  y: number;
  z: number;
  baseX: number;
  baseY: number;
  size: number;
  speed: number;
  angle: number;
  orbitRadius: number;
  orbitSpeed: number;
  opacity: number;
}

export default function ParticleNetwork({ mouseX, mouseY }: ParticleNetworkProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animationRef = useRef<number>(0);
  const mouseRef = useRef({ x: mouseX, y: mouseY });

  // Update mouse position ref
  useEffect(() => {
    mouseRef.current = { x: mouseX, y: mouseY };
  }, [mouseX, mouseY]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const resizeCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Initialize particles
    const particleCount = 120;
    const centerX = canvas.width / (window.devicePixelRatio || 1) / 2;
    const centerY = canvas.height / (window.devicePixelRatio || 1) / 2;

    particlesRef.current = Array.from({ length: particleCount }, () => {
      const angle = Math.random() * Math.PI * 2;
      const orbitRadius = 80 + Math.random() * 250;
      return {
        x: centerX + Math.cos(angle) * orbitRadius,
        y: centerY + Math.sin(angle) * orbitRadius,
        z: Math.random() * 2 - 1, // -1 to 1 for 3D depth
        baseX: centerX,
        baseY: centerY,
        size: Math.random() * 3 + 1,
        speed: 0.0005 + Math.random() * 0.001,
        angle: angle,
        orbitRadius: orbitRadius,
        orbitSpeed: (0.2 + Math.random() * 0.5) * (Math.random() > 0.5 ? 1 : -1),
        opacity: 0.3 + Math.random() * 0.7,
      };
    });

    // Animation loop
    let time = 0;
    const animate = () => {
      const width = canvas.width / (window.devicePixelRatio || 1);
      const height = canvas.height / (window.devicePixelRatio || 1);

      ctx.clearRect(0, 0, width, height);

      const mouse = mouseRef.current;
      const mouseCenterX = mouse.x * width;
      const mouseCenterY = mouse.y * height;

      time += 0.016; // ~60fps

      // Update and draw particles
      particlesRef.current.forEach((particle, index) => {
        // Update angle for orbital motion
        particle.angle += particle.orbitSpeed * 0.01;

        // Calculate 3D position with whirl effect
        const whirlFactor = Math.sin(time * 0.5 + index * 0.1) * 0.3;
        const dynamicRadius = particle.orbitRadius * (1 + whirlFactor * 0.2);

        // Base position (orbital)
        const baseX = width / 2 + Math.cos(particle.angle) * dynamicRadius;
        const baseY = height / 2 + Math.sin(particle.angle) * dynamicRadius * 0.6; // Flatten for perspective

        // Add mouse influence
        const dx = mouseCenterX - baseX;
        const dy = mouseCenterY - baseY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const maxDist = 200;
        const influence = Math.max(0, 1 - dist / maxDist) * 30;

        particle.x = baseX + (dx / dist || 0) * influence;
        particle.y = baseY + (dy / dist || 0) * influence;

        // 3D depth effect (z oscillation)
        particle.z = Math.sin(time * 0.8 + particle.angle * 2) * 0.5;
        const scale = 1 + particle.z * 0.3;
        const depthOpacity = 0.4 + (particle.z + 1) * 0.3;

        // Draw particle
        const size = particle.size * scale;
        const gradient = ctx.createRadialGradient(
          particle.x,
          particle.y,
          0,
          particle.x,
          particle.y,
          size * 2
        );

        // Cyan glow color
        const alpha = particle.opacity * depthOpacity;
        gradient.addColorStop(0, `rgba(0, 212, 255, ${alpha})`);
        gradient.addColorStop(0.5, `rgba(0, 212, 255, ${alpha * 0.5})`);
        gradient.addColorStop(1, 'rgba(0, 212, 255, 0)');

        ctx.beginPath();
        ctx.arc(particle.x, particle.y, size * 2, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();

        // Core dot
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, size * 0.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${alpha * 0.8})`;
        ctx.fill();
      });

      // Draw connecting lines between nearby particles
      ctx.strokeStyle = 'rgba(0, 212, 255, 0.1)';
      ctx.lineWidth = 0.5;

      for (let i = 0; i < particlesRef.current.length; i++) {
        for (let j = i + 1; j < particlesRef.current.length; j++) {
          const p1 = particlesRef.current[i];
          const p2 = particlesRef.current[j];
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < 80) {
            const opacity = (1 - dist / 80) * 0.15;
            ctx.strokeStyle = `rgba(0, 212, 255, ${opacity})`;
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        }
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationRef.current);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ opacity: 0.8 }}
    />
  );
}
